import { DatePipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpContextToken,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import {
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  signal,
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import type { MsalInterceptorConfiguration } from '@azure/msal-angular';
import {
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalService,
} from '@azure/msal-angular';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import { routes } from './app.routes';
import { getMsalConfig } from './core/auth/auth-config';
import { OutgoingInterceptor } from './core/auth/outgoing.interceptor';
import { Language } from './core/models/languages.model';
import { environment } from './environments/environment';

export const SKIP_LOADING = new HttpContextToken<boolean>(() => false);

export const currentLocale = signal(Language.ENGLISH.toString().toLowerCase());

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(getMsalConfig());
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.apiUrl, environment.B2E_scope);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    HttpClient,
    DatePipe,
    provideHttpClient(withInterceptorsFromDi()),
    provideIonicAngular(),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideNativeDateAdapter(),
    provideRouter(routes),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    { provide: HTTP_INTERCEPTORS, useClass: OutgoingInterceptor, multi: true },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: LOCALE_ID,
      useValue: currentLocale(),
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
  ],
};
